<template>
  <div class="single container">
    <div class="paddingX3">
      <common-flash-message />
    </div>
    <template v-if="canPurchaseByAmazonPay">
      <div class="textBold marginT5">Amazon Payをご利用の方はこちら</div>
      <div class="marginB5 amazonButtonWrap commonPurchase">
        <div id="AmazonPayButton"></div>
        <span class="commonPurchase-note textSmall marginT2"
          >Amazon.co.jp に登録している情報を使って簡単に街コンジャパンの登録・購入ができます。</span
        >
      </div>
    </template>
    <div v-if="$store.isLoggedIn" class="marginT5">
      <div class="textBold">
        クレカ、キャリア決済、コンビニ<template v-if="isChocomEmoneyAvailable">、ちょコムeマネー</template>をご利用の方はこちら
      </div>
      <custom-nuxt-link
        :to="`/events/${$route.params.code}/tickets/${$route.params.ticket_id}/purchases/new?quantity=${$route.query.quantity || 1}`"
        class="commonButton btn-block btn-primary-50 commonPurchase"
      >
        <span>通常決済</span>
        <span class="commonPurchase-note textSmall">machicon JAPANアカウントでお支払い</span>
      </custom-nuxt-link>
    </div>
    <template v-else>
      <div class="marginT5">
        <div class="textBold">初めての方はこちら</div>
        <div class="commonButton btn-block btn-primary-50" @click="toGuestPurchaseNew($route.query.quantity || 1)">登録・予約に進む</div>
      </div>
      <div class="marginT5">
        <div class="textBold">会員の方はこちら</div>
        <a :href="existingUserPurchaseUrl" class="commonBlueButtonTop large"> ログインして予約に進む </a>
      </div>
    </template>
    <button class="backlink" @click="browserBackToEventDetail">戻る</button>
  </div>
</template>

<script>
import { buildQueryString } from '@/utils/url-action'
import Cookies from 'mj-cookie'
import { AMAZON_PAY_PUBLIC_KEY_ID, AMAZON_PAY_MERCHANT_ID, AMAZON_PAY_CHECKOUT_JS_URL } from '@/utils/settings'
import { COOKIE_AMAZON_PURCHASE_PATH, COOKIE_AMAZON_PURCHASE_QUANTITY } from '@/utils/constants/amazonPay'
import OnlineEventMixin from '@/utils/mixins/OnlineEventMixin'
import BrowserBackMixin from '@/utils/mixins/BrowserBackMixin'
import ChocomEmoneyMixin from '@/utils/mixins/ChocomEmoneyMixin'
const CommonFlashMessage = defineAsyncComponent(() => import('@/components/CommonFlashMessage.vue'))
import { useMainStore } from '@/store'
import { callWithNuxt } from 'nuxt/app'
export default {
  components: {
    CommonFlashMessage
  },
  mixins: [OnlineEventMixin, BrowserBackMixin, ChocomEmoneyMixin],
  setup() {
    const route = useRoute()
    const store = useMainStore()
    const app = useNuxtApp()
    const query = {}
    const myProfile = store.$state.my_profile
    query.quantity = route.query.quantity || 1
    definePageMeta({
      middleware: [
        function (to, _from) {
          const store = useMainStore()
          const isSpecialPass =
            to.params.code === import.meta.env.KONKATSU_FREEPASS_EVENT_CODE || to.params.code === import.meta.env.MENSPASS_EVENT_CODE
          if ((store.$state.auth_info && store.$state.native_app) || isSpecialPass) {
            navigateTo(`/events/${to.params.code}/tickets/${to.params.ticket_id}/purchases/new/?quantity=${to.query.quantity || 1}`)
          }
        },
        function (to, _from) {
          const store = useMainStore()
          const { abGroup } = usePaymentFlowABTest()

          if (abGroup.value !== 'B') return
          const errorCode = to.query.error_code

          if (errorCode && store.$state.auth_info) {
            return navigateTo({
              path: `/events/${to.params.code}/tickets/${to.params.ticket_id}/purchases/new/`,
              query: {
                quantity: to.query.quantity || 1,
                error_code: errorCode
              }
            })
          }

          const eventQuery = errorCode ? { error_code: errorCode } : undefined
          return navigateTo({
            path: `/events/${to.params.code}/`,
            query: eventQuery
          })
        }
      ],
      layout: 'simple'
    })
    useNoIndex()
    if (!myProfile || !myProfile.filled_profile || !myProfile.name) {
      query.client_redirect_path = route.fullPath
    }
    return store
      .dispatch('apiGet', `events/${route.params.code}/tickets/${route.params.ticket_id}/purchase_options/${buildQueryString(query)}`)
      .then(res => {
        if (res.data.redirect_path) {
          return callWithNuxt(app, () => navigateTo(`${res.data.redirect_path}?quantity=${route.query.quantity || 1}`, { replace: true }))
        } else {
          return res.data
        }
      })
      .catch(async e => {
        const errorCode = parseInt(e.response && e.response.status)
        await app.$error(errorCode)
      })
  },
  data() {
    return {
      ticket: {}
    }
  },
  computed: {
    existingUserPurchaseUrl() {
      const purchasesNewPath = `/events/${this.$route.params.code}/tickets/${this.$route.params.ticket_id}/purchases/new?quantity=${this.$route.query.quantity || 1}`
      const redirectPathQuery = `cb_redirect_path=${encodeURIComponent(purchasesNewPath)}`
      return `${import.meta.env.LINKBAL_ID_IDP_URL}/v2/login?client_id=${import.meta.env.LINKBAL_ID_CLIENT_ID}&${redirectPathQuery}`
    },
    canPurchaseByAmazonPay() {
      return !(this.isFiveMinutesEvent || this.ticket.price === 0 || this.$store.$state.native_app)
    }
  },
  mounted() {
    if (this.canPurchaseByAmazonPay) {
      this.loadAmazonCheckoutLibrary()
    }
    this.setupCookie()
  },
  methods: {
    setupCookie() {
      const purchasePath = `/events/${this.$route.params.code}/tickets/${this.$route.params.ticket_id}`
      const purchaseQuantity = this.$route.query.quantity || 1
      Cookies.set(COOKIE_AMAZON_PURCHASE_PATH, purchasePath, { expires: 1 }) // expires time 1 day
      Cookies.set(COOKIE_AMAZON_PURCHASE_QUANTITY, purchaseQuantity, { expires: 1 }) // expires time 1 day
    },
    renderAmazonPayButton() {
      const scriptTag = document.createElement('script')
      const scriptContent = document.createTextNode(
        `amazon.Pay.renderButton('#AmazonPayButton', {
          merchantId: '${AMAZON_PAY_MERCHANT_ID}',
          publicKeyId: '${AMAZON_PAY_PUBLIC_KEY_ID}',
          ledgerCurrency: 'JPY',
          checkoutLanguage: 'ja_JP',
          productType: 'PayOnly',
          placement: 'Other',
          buttonColor: 'Gold',
          createCheckoutSessionConfig: {
            payloadJSON: '${this.amzpay_button_payload}',
            signature: '${this.amzpay_button_signature}'
          }
        });`
      )
      scriptTag.appendChild(scriptContent)
      document.body.appendChild(scriptTag)
    },
    loadAmazonCheckoutLibrary() {
      const script = document.createElement('script')
      script.onload = () => {
        this.renderAmazonPayButton()
      }
      script.setAttribute('src', AMAZON_PAY_CHECKOUT_JS_URL)
      script.setAttribute('defer', '')
      document.body.appendChild(script)
    }
  }
}
</script>

<style lang="scss" scoped>
.container {
  position: relative;
  padding: 0 1.5rem;
}

.backlink {
  position: absolute;
  bottom: 0;
  font-size: 1.2rem;
  color: $gray-55;
  border: none;
  background: none;
  cursor: pointer;
}

.commonPurchase {
  display: block;
  text-align: center;
  &-note {
    display: block;
  }
  &-icon {
    display: block;
  }
}

.amazonButtonWrap {
  padding: 1.6rem 1rem;
  border: 1px solid $gray-c6;
  border-radius: 3px;
  #AmazonPayButton {
    margin: auto;
  }
}
</style>
